import type { Session, User } from '@supabase/supabase-js';
import { getSupabaseFunctionHttpStatus } from '@/lib/supabase-function-errors';
import type { AuthRecoveryOutcome, AuthRecoveryState } from './AuthContext';

interface AuthStateSnapshot {
  session: Session | null;
  user: User | null;
}

interface RefreshAuthStateOptions extends AuthStateSnapshot {
  refreshedSession: Session | null;
  error: Error | null;
}

export const getRetainedAuthState = ({
  session,
  user,
}: AuthStateSnapshot): AuthStateSnapshot => ({
  session,
  user: user ?? session?.user ?? null,
});

export const resolveRefreshAuthState = ({
  session,
  user,
  refreshedSession,
  error,
}: RefreshAuthStateOptions): AuthStateSnapshot & { preserved: boolean } => {
  if (refreshedSession) {
    return {
      session: refreshedSession,
      user: refreshedSession.user,
      preserved: false,
    };
  }

  if (error) {
    return {
      ...getRetainedAuthState({ session, user }),
      preserved: true,
    };
  }

  return {
    session: null,
    user: null,
    preserved: false,
  };
};

/** A small shared state machine. Keeping this outside React makes concurrent
 * 401 behavior deterministic and testable. */
export class AuthRecoveryCoordinator {
  private state: AuthRecoveryState = 'ready';
  private flight: Promise<AuthRecoveryOutcome> | null = null;

  constructor(
    private readonly refresh: () => Promise<boolean | AuthRecoveryOutcome>,
    private readonly onReauthRequired: () => void,
    private readonly onStateChange: (state: AuthRecoveryState) => void,
  ) {}

  getState = () => this.state;

  recover = (): Promise<AuthRecoveryOutcome> => {
    if (this.state === 'reauth_required') return Promise.resolve('reauth_required');
    if (this.flight) return this.flight;
    this.setState('refreshing');
    this.flight = this.refresh().then((result) => {
      const outcome: AuthRecoveryOutcome = result === true ? 'recovered' : result === false ? 'reauth_required' : result;
      if (outcome === 'recovered') {
        this.setState('ready');
        return 'recovered' as const;
      }
      if (outcome === 'retry_later') {
        this.setState('retry_later');
        return 'retry_later' as const;
      }
      this.setState('reauth_required');
      this.onReauthRequired();
      return 'reauth_required' as const;
    }).catch(() => {
      this.setState('retry_later');
      return 'retry_later' as const;
    }).finally(() => { this.flight = null; });
    return this.flight;
  };

  run = async <T>(request: () => Promise<T>): Promise<T> => {
    try {
      return await request();
    } catch (error) {
      // Do not replay fetch/network errors: whether a mutation reached the
      // server is unknowable. Only a response-confirmed 401 is safe to retry.
      if (getSupabaseFunctionHttpStatus(error) !== 401) throw error;
      const outcome = await this.recover();
      if (outcome !== 'recovered') throw error;
      return request();
    }
  };

  private setState(state: AuthRecoveryState) {
    this.state = state;
    this.onStateChange(state);
  }
}

/** Refresh token invalidation is terminal; a transport/5xx/unknown Auth error
 * is not evidence of revocation and must leave local work in place to retry. */
export function classifyRefreshOutcome(session: Session | null, error: { status?: unknown; message?: unknown } | null): AuthRecoveryOutcome {
  if (session) return 'recovered';
  if (!error) return 'reauth_required';
  const status = typeof error.status === 'number' ? error.status : null;
  const message = typeof error.message === 'string' ? error.message.toLowerCase() : '';
  const definitelyRevoked = (status === 400 || status === 401 || status === 403) &&
    /(refresh token|session).*(invalid|expired|not found|revoked)|invalid.*refresh token/.test(message);
  return definitelyRevoked ? 'reauth_required' : 'retry_later';
}

export function normalizeAuthReturnPath(value: string | null | undefined, origin = typeof window === 'undefined' ? '' : window.location.origin): string {
  if (!value || !origin) return '/applications';
  try {
    const url = new URL(value, origin);
    return url.origin === origin && url.pathname.startsWith('/')
      ? `${url.pathname}${url.search}${url.hash}`
      : '/applications';
  } catch {
    return '/applications';
  }
}
